import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { maskCPF, unmaskCPF, maskData, convertDataToISO, convertISOToData } from "@/lib/utils";
import { Camera, User, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { CriarPacienteFormData } from "../schemas/paciente.schema";
import { AbaPessoaProps } from "../interface/Paciente.interface";

export const AbaPessoa = ({ form, formData }: { form: UseFormReturn<CriarPacienteFormData>; formData: CriarPacienteFormData }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dataNascimentoFormatada, setDataNascimentoFormatada] = useState<string>("");
  const [dataNascimentoErro, setDataNascimentoErro] = useState<string>("");

  useEffect(() => {
    if (isModalOpen && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [isModalOpen, stream]);

  // Carrega a data de nascimento formatada quando o formData muda (ao editar)
  useEffect(() => {
    if (formData.pessoa.data_nascimento) {
      const dataFormatada = convertISOToData(formData.pessoa.data_nascimento);
      setDataNascimentoFormatada(dataFormatada);
    } else {
      setDataNascimentoFormatada("");
    }
  }, [formData.pessoa.data_nascimento]);

  const iniciarCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }
      });
      setStream(mediaStream);
    } catch (error) {
      console.error("Erro ao acessar a câmera:", error);
      alert("Não foi possível acessar a câmera. Verifique as permissões.");
    }
  };

  const pararCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const tirarFoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Captura em formato quadrado (ideal para avatar)
      const tamanho = Math.min(video.videoWidth, video.videoHeight);
      canvas.width = tamanho;
      canvas.height = tamanho;

      if (context) {
        // Calcula o ponto de partida para centralizar a imagem
        const offsetX = (video.videoWidth - tamanho) / 2;
        const offsetY = (video.videoHeight - tamanho) / 2;

        // Desenha a imagem centralizada e cortada em formato quadrado
        context.drawImage(
          video,
          offsetX, offsetY, tamanho, tamanho,
          0, 0, tamanho, tamanho
        );
        
        const fotoDataUrl = canvas.toDataURL('image/png');
        setFotoUrl(fotoDataUrl);
      }
    }
  };

  const confirmarFoto = () => {
    if (fotoUrl) {
      form.setValue("pessoa.imagem_perfil", fotoUrl);
    }
    pararCamera();
    setIsModalOpen(false);
  };

  const cancelarFoto = () => {
    setFotoUrl(null);
    pararCamera();
    setIsModalOpen(false);
  };

  const abrirModal = () => {
    setIsModalOpen(true);
    iniciarCamera();
  };

  const fecharModal = () => {
    setFotoUrl(null);
    pararCamera();
    setIsModalOpen(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-4">
          <Avatar 
            className="h-16 w-16 cursor-pointer border-2 border-primary hover:opacity-80 transition-opacity flex-shrink-0"
            onClick={abrirModal}
          >
            <AvatarImage 
              src={fotoUrl || formData.pessoa.imagem_perfil} 
              alt="Foto do paciente" 
              className="object-cover"
            />
            <AvatarFallback className="bg-primary/10">
              <User className="h-8 w-8 text-primary" />
            </AvatarFallback>
          </Avatar>
          <CardTitle>Dados Pessoais</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="pessoa.nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Nome Completo <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input 
                  {...field}
                  placeholder="Digite o nome completo"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="pessoa_fisica.cpf"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  CPF <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    {...field}
                    value={maskCPF(field.value || "")}
                    onChange={(e) => field.onChange(unmaskCPF(e.target.value))}
                    maxLength={14}
                    placeholder="000.000.000-00"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="pessoa_fisica.rg"
            render={({ field }) => (
              <FormItem>
                <FormLabel>RG</FormLabel>
                <FormControl>
                  <Input 
                    {...field}
                    value={field.value || ""}
                    maxLength={20}
                    placeholder="00.000.000-0"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="pessoa.data_nascimento"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data de Nascimento</FormLabel>
                <FormControl>
                  <Input 
                    id="data_nascimento"
                    type="text"
                    value={dataNascimentoFormatada || (field.value ? convertISOToData(field.value) : "")}
                    className={dataNascimentoErro ? "border-red-500" : ""}
                    onChange={(e) => {
                      const valorFormatado = maskData(e.target.value);
                      setDataNascimentoFormatada(valorFormatado);
                      setDataNascimentoErro("");
                    }}
                    onBlur={(e) => {
                      const valor = e.target.value.trim();
                      
                      if (!valor || valor.length === 0) {
                        setDataNascimentoErro("");
                        field.onChange("");
                        setDataNascimentoFormatada("");
                        field.onBlur();
                        return;
                      }
                      
                      if (valor.length < 10) {
                        setDataNascimentoErro("Data incompleta. Use o formato dd/MM/yyyy");
                        field.onBlur();
                        return;
                      }
                      
                      if (valor.length === 10) {
                        const dataISO = convertDataToISO(valor);
                        if (!dataISO) {
                          setDataNascimentoErro("Data inválida. Exemplo: 31/12/1990");
                        } else {
                          setDataNascimentoErro("");
                          field.onChange(dataISO);
                        }
                      }
                      
                      field.onBlur();
                    }}
                    placeholder="dd/MM/yyyy (ex: 31/12/1990)"
                    maxLength={10}
                  />
                </FormControl>
                {dataNascimentoErro && (
                  <p className="text-sm text-red-500">{dataNascimentoErro}</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="pessoa.genero"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Gênero</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ""}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o gênero" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="MASCULINO">Masculino</SelectItem>
                    <SelectItem value="FEMININO">Feminino</SelectItem>
                    <SelectItem value="OUTRO">Outro</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </CardContent>

      <Dialog open={isModalOpen} onOpenChange={fecharModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Capturar Foto</DialogTitle>
            <DialogDescription>
              Posicione-se em frente à câmera e clique em "Tirar Foto"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {fotoUrl ? (
              <div className="space-y-4">
                <div className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src={fotoUrl} 
                    alt="Preview da foto" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={confirmarFoto} 
                    className="flex-1"
                  >
                    Confirmar Foto
                  </Button>
                  <Button 
                    onClick={() => {
                      setFotoUrl(null);
                      iniciarCamera();
                    }} 
                    variant="outline"
                    className="flex-1"
                  >
                    Tirar Outra
                  </Button>
                  <Button 
                    onClick={cancelarFoto} 
                    variant="ghost"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative w-full aspect-square bg-gray-900 rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={tirarFoto} 
                    className="flex-1"
                    disabled={!stream}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Tirar Foto
                  </Button>
                  <Button 
                    onClick={cancelarFoto} 
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <canvas ref={canvasRef} className="hidden" />
        </DialogContent>
      </Dialog>
    </Card>
  );
};
